
import $ from "jquery"
import serialize from "form-serialize"

import { EventEmitter } from "events"
import Promise from "plugins/bluebird"

import Notifier from "app/notifier/index"

import Auth_action from "app/authentication/action"
import Auth_callback_queue from "app/authentication/callback_queue"

import {
  refreshAndUpdateComponentPromise,
} from "app/authentication/remote_state"

import {
  captureMessage,
  captureException,
} from "app/error_reporting/index"

import {
  getContactInfoData,
  setContactInfoData,
} from "app/local_storages/contact_info_storage/index"

import {
  create as create_user,
  ResultTypeEnums as CreateUserResultTypes,
} from "app/resources/users/create/index"

import {
  isPresentString,
} from "app/utils/type_checking/index"

import {
  isSignedIn,
} from "app/authentication/state"

import {
  create as create_city_specific_subscription,
  ResultTypeEnums as CreateCitySpecificSubscriptionResultTypes,
} from "app/resources/city_specific_listing_update_subscription/create/index"

import {
  destroy as destroy_city_specific_subscription,
  ResultTypeEnums as DestroyCitySpecificSubscriptionResultTypes,
} from "app/resources/city_specific_listing_update_subscription/destroy/index"


import {
  trackSignUp,
} from "app/authentication/tracking/index"

import {
  getLastContactInfoSyncPromise,
} from "app/authentication/contact_info_caching/index"

import { track as Google_Event_track } from "app/tracking/google/event"
import { PageInstance } from "app/page_instance"



const RejectionReasons = {
  ERROR_REPORTED: "ERROR_REPORTED",
}

import I18n from "plugins/i18n-js"

const STATE_CLASS_FOR_WORKING = "city-update-subscription-button--working"
const STATE_CLASS_FOR_FOLLOWED = "city-update-subscription-button--following"


interface Selector {
  main: string | JQuery

  all_containers_on_page: string
  follow_button:          string
  following_button:       string
}

interface InputSelector {
  main: string | JQuery

  all_containers_on_page?: string
  follow_button?:          string
  following_button?:       string
}

const default_selector = {
  main: $(),
  all_containers_on_page: ".cell--buildings--overseas-marketing--city-update-subscription--button-form",
  follow_button: ".cell--buildings--overseas-marketing--city-update-subscription--button--follow",
  following_button: ".cell--buildings--overseas-marketing--city-update-subscription--button--following",
}


const SOURCE_FILE_PATH = "app/assets/javascripts/concepts/buildings/overseas_marketing/city_update_subscription/button/factory.es6"


class CityUpdateSubscriptionButton extends EventEmitter {
  private selector: Selector

  private $el: JQuery
  private $follow_button: JQuery
  private $following_button: JQuery

  constructor(selector: InputSelector){
    super()

    this.selector = Object.assign({}, default_selector, selector)

    this.$el = $(this.selector.main)

    this.$follow_button = this.$el.find(this.selector.follow_button)
    this.$following_button = this.$el.find(this.selector.following_button)

    if (this.$el.length === 0) {
      return
    }

    this.bind_action()
  }


  private bind_action(){
    this.$follow_button.on("click", (e) => {
      e.preventDefault()

      this.onFollowSubmit()
    })

    this.$following_button.on("click", (e) => {
      e.preventDefault()

      this.onUnFollowSubmit()
    })

    return this
  }
  private onUnFollowSubmit() {
    const local_contact_info_data = getContactInfoData()
    const serialized_form_data = serialize((this.$el[0] as HTMLFormElement), {hash: true}) as {
      city_update_subscription_form?: {
        city_id: any
      }
    }

    let city_id = null as null | string
    if (typeof serialized_form_data.city_update_subscription_form === "object" &&
      serialized_form_data.city_update_subscription_form.city_id != null) {
      city_id = serialized_form_data.city_update_subscription_form.city_id.toString()
    }
    if (typeof city_id !== "string") {
      captureMessage(
        "serialized_form_data.city_update_subscription_form.city_id is not string",
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
      return
    }

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      Auth_callback_queue.add(() => {
        getLastContactInfoSyncPromise()
        .then(() => this.onUnFollowSubmit())
        .done()
      }, "event:unsubscribe_overseas_marketing_city_update")

      Auth_action.showSignUpModal({
        enable_account_already_exists_error_message: false,
      })
      .then(({$modal, authentication_box}) => {
        authentication_box.on("sign_up:fail:account_conflict", ({email_address}) => {
          $modal.modal("hide")
          // Retry AFTER setting local contact email
          setContactInfoData({
            contact_email_address: email_address,
          })
          this.onUnFollowSubmit()
        })
      })
      .done()
      return
    }

    Promise.resolve()
    .then(() => {

      if (isSignedIn()) {
        return Promise.resolve()
      }

      return create_user({
        authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        email_address: local_contact_info_data.contact_email_address || "",
        sign_in_on_success: true,
      })
      .then(({result_type}) => {
        switch (result_type) {
          case CreateUserResultTypes.SUCCESS:
            trackSignUp()
            refreshAndUpdateComponentPromise().done()
            return
          case CreateUserResultTypes.CONFLICT:
            return
          default:
            captureMessage(
              `unexpected/unhandled result_type: <${result_type}>`,
              {
                source_file_path:
                  "app/assets/javascripts/concepts/listings/contact/button/events/index",
              }
            )
            throw RejectionReasons.ERROR_REPORTED
        }
      })
    })
    .then(() => {
      this.setFormStateAsWorking()
      if (typeof city_id !== "string") { throw new Error("city_id is not string") }
      return destroy_city_specific_subscription({
        // Need `||` to make flow happy
        user_authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        city_id: city_id,
      })
    })
    .then(({result_type}) => {
      switch (result_type) {
        case DestroyCitySpecificSubscriptionResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from destroy_city_specific_subscription: <${result_type}>`,
            {
              source_file_path:
              SOURCE_FILE_PATH,
            }
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      this.setFormStateAsDefault()

      this.emit("form_submission_succeeded")

      const location_name = this.$el.data("location-name")
      Notifier.showSuccess(I18n.t(`concepts.buildings.overseas_marketing.city_update_subscription.button.js.unfollowed`, {location_name: location_name}))
    })
    .catch((whatever) => {
      this.setFormStateAsFollowed()
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }
  private onFollowSubmit() {
    const local_contact_info_data = getContactInfoData()
    const serialized_form_data = serialize((this.$el[0] as HTMLFormElement), {hash: true}) as {
      city_update_subscription_form?: {
        city_id: any
      }
    }

    let city_id = null as null | string
    if (typeof serialized_form_data.city_update_subscription_form === "object" &&
      serialized_form_data.city_update_subscription_form.city_id != null) {
      city_id = serialized_form_data.city_update_subscription_form.city_id.toString()
    }
    if (typeof city_id !== "string") {
      captureMessage(
        "serialized_form_data.city_update_subscription_form.city_id is not string",
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
      return
    }

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      Auth_callback_queue.add(() => {
        getLastContactInfoSyncPromise()
        .then(() => this.onFollowSubmit())
        .done()
      }, "event:subscribe_overseas_marketing_city_update")

      Auth_action.showSignUpModal({
        enable_account_already_exists_error_message: false,
      })
      .then(({$modal, authentication_box}) => {
        authentication_box.on("sign_up:fail:account_conflict", ({email_address}) => {
          $modal.modal("hide")
          // Retry AFTER setting local contact email
          setContactInfoData({
            contact_email_address: email_address,
          })
          this.onFollowSubmit()
        })
      })
      .done()
      return
    }

    Promise.resolve()
    .then(() => {

      if (isSignedIn()) {
        return Promise.resolve()
      }

      return create_user({
        authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        email_address: local_contact_info_data.contact_email_address || "",
        sign_in_on_success: true,
      })
      .then(({result_type}) => {
        switch (result_type) {
          case CreateUserResultTypes.SUCCESS:
            trackSignUp()
            refreshAndUpdateComponentPromise().done()
            return
          case CreateUserResultTypes.CONFLICT:
            return
          default:
            captureMessage(
              `unexpected/unhandled result_type: <${result_type}>`,
              {
                source_file_path:
                  "app/assets/javascripts/concepts/listings/contact/button/events/index",
              }
            )
            throw RejectionReasons.ERROR_REPORTED
        }
      })
    })
    .then(() => {
      this.setFormStateAsWorking()
      if (typeof city_id !== "string") { throw new Error("city_id is not string") }
      return create_city_specific_subscription({
        // Need `||` to make flow happy
        user_authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        city_id: city_id,
      })
    })
    .then(({result_type}) => {
      switch (result_type) {
        case CreateCitySpecificSubscriptionResultTypes.SUCCESS:
          return
        case CreateCitySpecificSubscriptionResultTypes.RESOURCE_CREATED:
          Google_Event_track({
            category: PageInstance.currentPageNameForTracking(),
            action:   "Follow",
            options:  {
              eventLabel: "Follow City - Followed",
            },
          })
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from create_city_specific_subscription: <${result_type}>`,
            {
              source_file_path:
              SOURCE_FILE_PATH,
            }
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      this.setFormStateAsFollowed()

      this.emit("form_submission_succeeded")

      const location_name = this.$el.data("location-name")
      Notifier.showSuccess(I18n.t(`concepts.buildings.overseas_marketing.city_update_subscription.button.js.followed`, {location_name: location_name}))
    })
    .catch((whatever) => {
      this.setFormStateAsDefault()
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }
  private setFormStateAsDefault() {
    $(default_selector.all_containers_on_page)
    .removeClass(
      STATE_CLASS_FOR_FOLLOWED
    )
    .removeClass(
      STATE_CLASS_FOR_WORKING
    )
  }
  private setFormStateAsWorking() {
    $(default_selector.all_containers_on_page)
    .removeClass(
      STATE_CLASS_FOR_FOLLOWED
    )
    .addClass(
      STATE_CLASS_FOR_WORKING
    )
  }
  private setFormStateAsFollowed() {
    $(default_selector.all_containers_on_page)
    .removeClass(
      STATE_CLASS_FOR_WORKING
    )
    .addClass(
      STATE_CLASS_FOR_FOLLOWED
    )
  }
}

const Factory = {
  create(selector: InputSelector){
    return new CityUpdateSubscriptionButton(selector)
  },
}

export {
  Factory,
}
